import * as THREE from 'three';
import type { Vec3 } from '../api/types.js';

export interface FitCameraResult {
  pos: Vec3;
  lookAt: Vec3;
  fov: number;
}

// Helper condiviso (Inc B, Codex): center + size del Box3, null se vuoto o con
// estensione zero.
function boxCenterSize(
  box: THREE.Box3,
): { center: THREE.Vector3; size: THREE.Vector3 } | null {
  if (box.isEmpty()) return null;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  if (Math.max(size.x, size.y, size.z) <= 0) return null;
  return { center, size };
}

// Pure helper testabile separato dall'adapter. Computes a sensible camera
// position to fit a Box3 in view, given a perspective FOV. Returns null if
// the box is empty or has zero extent.
// Position diagonal up + back from center for a 3/4 view that shows depth.
export function fitCameraFromBox3(box: THREE.Box3, fov: number): FitCameraResult | null {
  const cs = boxCenterSize(box);
  if (cs === null) return null;
  const { center, size } = cs;
  const maxDim = Math.max(size.x, size.y, size.z);
  const distance = maxDim / 2 / Math.tan((fov * Math.PI) / 360);
  const pos: Vec3 = [
    center.x + distance * 0.7,
    center.y + distance * 0.5,
    center.z + distance * 0.7,
  ];
  return { pos, lookAt: [center.x, center.y, center.z], fov };
}

// Inc B — Overview / dollhouse: vista elevata "modello su tavolo" che inquadra
// l'INTERO splat. La distanza è calcolata dal raggio della bounding-sphere del
// bbox (mezza diagonale) → garantisce che ogni vertice resti in frame da
// qualsiasi angolo (Codex review): una sfera di raggio r entra nel FOV
// verticale a distanza r/sin(fov/2); il margine dà respiro ai bordi. La
// direzione è elevata (Y dominante) per il colpo d'occhio d'insieme.
// Returns null se il box è vuoto o ha estensione zero.
export function overviewCameraFromBox3(box: THREE.Box3, fov: number): FitCameraResult | null {
  const cs = boxCenterSize(box);
  if (cs === null) return null;
  const { center, size } = cs;
  const radius = 0.5 * Math.hypot(size.x, size.y, size.z);
  const margin = 1.15;
  const distance = (radius / Math.sin((fov * Math.PI) / 360)) * margin;
  const dir = new THREE.Vector3(0.45, 1.05, 0.45).normalize();
  const pos: Vec3 = [
    center.x + dir.x * distance,
    center.y + dir.y * distance,
    center.z + dir.z * distance,
  ];
  return { pos, lookAt: [center.x, center.y, center.z], fov };
}
